import { useState, useEffect, useMemo, useCallback } from 'react';
import { useAppContext } from 'src/contexts/AppContext';
import {
  kpiDataService,
  KpiMetric,
  useKpiSourcesQuery,
} from 'src/api/startModelingService';
import { useCampaignMappingsQuery } from 'src/api/campaignMappings';
import { useCampaignGroupsQuery } from 'src/api/campaignGroups';
import {
  useCreateModelMutation,
  useUpdateModelMutation,
  useModelDetailsQuery,
  useModelsQuery,
  CreateModelInput,
} from 'src/api/models';
import type { ModelType } from 'src/api/models';
import { useCustomToast } from 'src/hooks/useCustomToast';
import { sourceHasDerivedUnits } from 'src/features/Settings/utils/constants';

export type ModelFormData = {
  id: string;
  modelName: string;
  modelType?: ModelType;
  kpiSource: string;
  kpiMetric: string;
  kpiUnit: string;
  selectedMedia: string[];
  campaignSelections: Record<string, boolean>;
  /** Long-term models only: the parent MM model the LT derives from. */
  parentModelId?: string;
};

export type CampaignWithGroup = {
  campaignId: string;
  campaignName: string;
  groupId: string | null;
  groupName: string | null;
};

const createEmptyModel = (id: string): ModelFormData => ({
  id,
  modelName: '',
  kpiSource: '',
  kpiMetric: '',
  kpiUnit: '',
  selectedMedia: [],
  campaignSelections: {},
});

// Map database table names to source identifiers used by the KPI discovery API
const TABLE_TO_SOURCE_MAP: Record<string, string> = {
  u_kpi_data_template: 'uploaded_file',
  d_shopify: 'shopify',
  d_ga4: 'ga4',
};

const mapTableToSource = (tableName: string | null): string => {
  if (!tableName) return '';
  return TABLE_TO_SOURCE_MAP[tableName] || tableName;
};

type UseModelFormOptions = {
  mode: 'create' | 'edit';
  modelId?: string;
  onComplete?: () => void;
};

export const useModelForm = ({
  mode,
  modelId,
  onComplete,
}: UseModelFormOptions) => {
  const { currentCustomer } = useAppContext();
  const customerId = currentCustomer?.customerName ?? '';
  const { toastState, hideToast, showSuccess, showError } = useCustomToast();

  // Models state (array for create, single for edit)
  const [models, setModels] = useState<ModelFormData[]>([
    createEmptyModel('1'),
  ]);
  const [kpiMetrics, setKpiMetrics] = useState<KpiMetric[]>([]);
  const [isLoadingKpi, setIsLoadingKpi] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  // Queries
  const { data: kpiSources = [], isLoading: isLoadingKpiSources } =
    useKpiSourcesQuery(customerId);
  const { data: campaignMappings = [], isLoading: isLoadingCampaigns } =
    useCampaignMappingsQuery(customerId);
  const { data: campaignGroups = [], isLoading: isLoadingGroups } =
    useCampaignGroupsQuery(customerId);
  const { data: modelDetails, isLoading: isLoadingModel } =
    useModelDetailsQuery(
      customerId,
      mode === 'edit' ? (modelId ?? null) : null,
    );
  const {
    data: allModels = [],
    isLoading: isLoadingAllModels,
    isError: isAllModelsError,
  } = useModelsQuery(customerId);

  // Mutations
  const createMutation = useCreateModelMutation(customerId);
  const updateMutation = useUpdateModelMutation(customerId);

  // Eligible parents for a long-term model: successful, published Full MM
  // models. The LT model inherits KPI/media/campaigns from the parent, so
  // only models whose results actually exist qualify.
  const eligibleLtParents = useMemo(
    () =>
      allModels.filter(
        (m) =>
          m.modelType === 'mm' &&
          !m.quickRun &&
          m.status === 'success' &&
          m.hasPublishedVersion === true,
      ),
    [allModels],
  );

  // When editing an LT model, its own pair must not block anything; the
  // reason is only about parent availability. Split by query state so the
  // "publish one first" copy never flashes while models are still loading
  // (or after a failed load).
  let ltDisabledReason: string | undefined;
  if (isLoadingAllModels) {
    ltDisabledReason = 'Checking for available Marketing Mix models…';
  } else if (isAllModelsError) {
    ltDisabledReason =
      'Could not load your models to pick a parent from — please try again';
  } else if (eligibleLtParents.length === 0) {
    ltDisabledReason =
      'Long-term models need a successful, published Marketing Mix model to build on — create and publish one first';
  }

  // Derive media platforms from campaign mappings
  const mediaPlatforms = useMemo(() => {
    const sourceMap = new Map<string, string>();

    for (const campaign of campaignMappings) {
      for (const sourceItem of campaign.sources) {
        const source = sourceItem.source?.trim();
        if (source) {
          const key = source.toLowerCase();
          const existing = sourceMap.get(key);
          if (
            !existing ||
            (source[0] === source[0].toUpperCase() &&
              existing[0] === existing[0].toLowerCase())
          ) {
            sourceMap.set(key, source);
          }
        }
      }
    }

    return Array.from(sourceMap.values()).sort((a, b) =>
      a.toLowerCase().localeCompare(b.toLowerCase()),
    );
  }, [campaignMappings]);

  // Derive campaigns with group info (for display in CampaignSelectList)
  const campaignsWithGroups: CampaignWithGroup[] = useMemo(() => {
    // Build a map of campaignId -> group info
    const campaignToGroup = new Map<
      string,
      { groupId: string; groupName: string }
    >();
    campaignGroups.forEach((group) => {
      group.campaignList.forEach((campaign) => {
        campaignToGroup.set(campaign.id, {
          groupId: group.id,
          groupName: group.groupName,
        });
      });
    });

    return campaignMappings.map((cm) => {
      const campaignId = cm.campaign_id.toString();
      const groupInfo = campaignToGroup.get(campaignId);
      return {
        campaignId,
        campaignName: cm.campaign_name,
        groupId: groupInfo?.groupId ?? null,
        groupName: groupInfo?.groupName ?? null,
      };
    });
  }, [campaignMappings, campaignGroups]);

  // Load KPI metrics for a source
  const loadKpiData = useCallback(
    async (source: string) => {
      if (!customerId || !source) return;

      setIsLoadingKpi(true);
      try {
        const metrics = await kpiDataService.getMetrics(customerId, source);
        setKpiMetrics(metrics);
      } catch {
        setKpiMetrics([]);
        showError('Failed to load KPI data', 'Please try again');
      } finally {
        setIsLoadingKpi(false);
      }
    },
    [customerId, showError],
  );

  // Initialize form for edit mode
  useEffect(() => {
    if (mode === 'edit' && modelDetails && !isInitialized) {
      const campaignSelections: Record<string, boolean> = {};
      modelDetails.campaigns.forEach((c) => {
        campaignSelections[c.campaignId] = !c.blacklist;
      });

      // Map the database table name to the source identifier
      const mappedKpiSource = mapTableToSource(modelDetails.kpiSource);

      setModels([
        {
          id: '1',
          modelName: modelDetails.modelName,
          modelType: modelDetails.modelType,
          kpiSource: mappedKpiSource,
          kpiMetric: modelDetails.kpiMetric || '',
          // Pre-populate from the stored value so a no-op re-save
          // keeps the model's kpi_unit unchanged
          kpiUnit: modelDetails.kpiUnit || '',
          selectedMedia: modelDetails.mediaPlatforms,
          campaignSelections,
          parentModelId: modelDetails.parentModelId ?? undefined,
        },
      ]);

      // Load KPI metrics and categories if source is set
      if (mappedKpiSource) {
        loadKpiData(mappedKpiSource);
      }

      setIsInitialized(true);
    }
  }, [mode, modelDetails, isInitialized, loadKpiData]);

  // Model handlers
  const updateModel = useCallback(
    (id: string, updates: Partial<ModelFormData>) => {
      setModels((prev) =>
        prev.map((m) => (m.id === id ? { ...m, ...updates } : m)),
      );
    },
    [],
  );

  const handleModelNameChange = useCallback(
    (id: string, value: string) => {
      updateModel(id, { modelName: value });
    },
    [updateModel],
  );

  const handleModelTypeChange = useCallback(
    (id: string, value: ModelType) => {
      // The parent reference only makes sense for LT models.
      updateModel(
        id,
        value === 'lt'
          ? { modelType: value }
          : { modelType: value, parentModelId: undefined },
      );
    },
    [updateModel],
  );

  const handleParentModelChange = useCallback(
    (id: string, parentModelId: string) => {
      updateModel(id, { parentModelId: parentModelId || undefined });
    },
    [updateModel],
  );

  const handleSourceChange = useCallback(
    async (id: string, value: string) => {
      updateModel(id, {
        kpiSource: value,
        kpiMetric: '',
        kpiUnit: '',
      });
      await loadKpiData(value);
    },
    [updateModel, loadKpiData],
  );

  const handleSourceReset = useCallback(
    (id: string) => {
      updateModel(id, {
        kpiSource: '',
        kpiMetric: '',
        kpiUnit: '',
      });
      setKpiMetrics([]);
    },
    [updateModel],
  );

  const handleMetricChange = useCallback((id: string, value: string) => {
    setModels((prev) =>
      prev.map((m) => {
        if (m.id !== id) return m;
        // Connector metrics (Shopify) have known semantics — the Unit
        // dropdown only offers the matching option, so clear any previously
        // chosen unit that may no longer be selectable for the new metric
        return {
          ...m,
          kpiMetric: value,
          ...(sourceHasDerivedUnits(m.kpiSource) ? { kpiUnit: '' } : {}),
        };
      }),
    );
  }, []);

  const handleMetricReset = useCallback((id: string) => {
    setModels((prev) =>
      prev.map((m) => {
        if (m.id !== id) return m;
        return {
          ...m,
          kpiMetric: '',
          // A derived unit belongs to the cleared metric — clear it too
          ...(sourceHasDerivedUnits(m.kpiSource) ? { kpiUnit: '' } : {}),
        };
      }),
    );
  }, []);

  const handleUnitChange = useCallback(
    (id: string, value: string) => {
      updateModel(id, { kpiUnit: value });
    },
    [updateModel],
  );

  const handleUnitReset = useCallback(
    (id: string) => {
      updateModel(id, { kpiUnit: '' });
    },
    [updateModel],
  );

  const handleMediaChange = useCallback(
    (id: string, selectedMedia: string[]) => {
      updateModel(id, { selectedMedia });
    },
    [updateModel],
  );

  const handleCampaignSelectionsChange = useCallback(
    (id: string, campaignSelections: Record<string, boolean>) => {
      updateModel(id, { campaignSelections });
    },
    [updateModel],
  );

  const handleAddModel = useCallback(() => {
    const newId = (models.length + 1).toString();
    const newModel = createEmptyModel(newId);
    // Pre-select all media and campaigns
    newModel.selectedMedia = [...mediaPlatforms];
    const campaignSelections: Record<string, boolean> = {};
    campaignsWithGroups.forEach((c) => {
      campaignSelections[c.campaignId] = true;
    });
    newModel.campaignSelections = campaignSelections;
    setModels((prev) => [...prev, newModel]);
  }, [models.length, mediaPlatforms, campaignsWithGroups]);

  const handleRemoveModel = useCallback((id: string) => {
    setModels((prev) => prev.filter((m) => m.id !== id));
  }, []);

  // Validation
  const isModelValid = useCallback((model: ModelFormData) => {
    const hasName = model.modelName.trim() !== '';
    const hasType = Boolean(model.modelType);
    // LT models inherit everything else from the parent — name + parent is
    // the whole form.
    if (model.modelType === 'lt') {
      return hasName && Boolean(model.parentModelId);
    }
    const hasSource = model.kpiSource !== '';
    const hasMetric = model.kpiMetric !== '';
    const hasUnit = model.kpiUnit === 'euro' || model.kpiUnit === 'unit';
    const hasMedia = model.selectedMedia.length > 0;
    const hasSelectedCampaigns = Object.values(model.campaignSelections).some(
      (v) => v === true,
    );
    return (
      hasName &&
      hasType &&
      hasSource &&
      hasMetric &&
      hasUnit &&
      hasMedia &&
      hasSelectedCampaigns
    );
  }, []);

  const areAllModelsValid = useMemo(
    () => models.every(isModelValid),
    [models, isModelValid],
  );

  // Submit handlers
  const handleSubmit = useCallback(() => {
    if (!areAllModelsValid) return;

    const modelsPayload: CreateModelInput[] = models.map((m) => {
      if (m.modelType === 'lt') {
        return {
          modelName: m.modelName.trim(),
          modelType: 'lt',
          parentModelId: m.parentModelId!,
        };
      }
      return {
        modelName: m.modelName.trim(),
        modelType: m.modelType as 'mm' | 'da',
        kpiSource: m.kpiSource,
        kpiMetric: m.kpiMetric,
        // Category dropdown was removed; unit is now sent explicitly
        kpiCategory: null,
        kpiUnit: m.kpiUnit as 'euro' | 'unit',
        selectedMedia: m.selectedMedia,
        campaignSelections: m.campaignSelections,
      };
    });

    if (mode === 'create') {
      createMutation.mutate(
        { models: modelsPayload },
        {
          onSuccess: () => {
            showSuccess(
              'Models created successfully',
              'Your models are now being processed.',
            );
            onComplete?.();
          },
          onError: (error) => {
            const defaultMessage =
              'Please try again or contact support if the issue persists';
            const apiMessage = (
              error as { response?: { data?: { message?: string } } }
            )?.response?.data?.message;
            const errorMessage =
              !apiMessage || apiMessage === 'Internal server error.'
                ? defaultMessage
                : apiMessage;
            showError('Failed to create models', errorMessage);
          },
        },
      );
    } else if (mode === 'edit' && modelId) {
      updateMutation.mutate(
        {
          modelId,
          ...modelsPayload[0],
        },
        {
          onSuccess: () => {
            showSuccess(
              'Model updated successfully',
              'Your model has been updated.',
            );
            onComplete?.();
          },
          onError: (error) => {
            const defaultMessage =
              'Please try again or contact support if the issue persists';
            const apiMessage = (
              error as { response?: { data?: { message?: string } } }
            )?.response?.data?.message;
            const errorMessage =
              !apiMessage || apiMessage === 'Internal server error.'
                ? defaultMessage
                : apiMessage;
            showError('Failed to update model', errorMessage);
          },
        },
      );
    }
  }, [
    areAllModelsValid,
    models,
    mode,
    modelId,
    createMutation,
    updateMutation,
    onComplete,
    showSuccess,
    showError,
  ]);

  const handleCancel = useCallback(() => {
    onComplete?.();
  }, [onComplete]);

  const isLoading =
    isLoadingKpiSources ||
    isLoadingCampaigns ||
    isLoadingGroups ||
    isLoadingModel ||
    isLoadingKpi;

  const isSubmitting = createMutation.isLoading || updateMutation.isLoading;

  return {
    // State
    models,
    kpiSources,
    kpiMetrics,
    mediaPlatforms,
    campaignsWithGroups,
    eligibleLtParents,
    ltDisabledReason,
    isLoading,
    isSubmitting,
    areAllModelsValid,

    // Toast state
    toastState,
    hideToast,

    // Handlers
    handleModelNameChange,
    handleModelTypeChange,
    handleParentModelChange,
    handleSourceChange,
    handleSourceReset,
    handleMetricChange,
    handleMetricReset,
    handleUnitChange,
    handleUnitReset,
    handleMediaChange,
    handleCampaignSelectionsChange,
    handleAddModel,
    handleRemoveModel,
    handleSubmit,
    handleCancel,
  };
};
